import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { LineController, UserRole } from '../../../model';
import { BackendService } from '../../../services/backend';
import { AuthenticationState } from '../../../store/auth/auth.state';

/**
 * Compact row of status indicators for the production line — machine power, line on, alarm,
 * bucket-in-scanner, continue request, eject request.
 *
 * Extracted from `app.component.html` so the header can stay focused on layout and so the
 * indicator group can be reused elsewhere (e.g. a diagnostics page).
 *
 * The "continue" indicator is interactive: clicking it toggles {@link LineController.continue}
 * (true→false / false→true) via {@link BackendService.setLine}. The action is gated to
 * {@link UserRole.Operator} / {@link UserRole.Admin}; a click by anyone else is a no-op.
 */
@Component({
  selector: 'app-line-status-indicators',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzSpaceModule, NzTagModule, NzTooltipModule, NzIconModule],
  templateUrl: './line-status-indicators.component.html',
  styleUrls: ['./line-status-indicators.component.scss'],
})
export class LineStatusIndicatorsComponent {
  private readonly backend = inject(BackendService);
  private readonly authState = inject(AuthenticationState);

  /** Current state of the production line (power, line on, alarm, bucket position, continue, eject). */
  readonly line = input.required<LineController>();

  /**
   * Toggle the line's `continue` flag through the backend. No-op for users without
   * {@link UserRole.Operator} / {@link UserRole.Admin}, and no-op while the line is
   * replaying (prevents mixed-reality interaction during IO recorder playback).
   * The HTTP call is fire-and-forget — the pushed line state comes back over SSE
   * and re-flows into {@link line}.
   */
  toggleContinue(): void {
    if (this.line().replaying) return;
    if (!this.authState.hasAnyRole(UserRole.Operator, UserRole.Admin)) return;
    this.backend.setLine({ continue: !this.line().continue }).subscribe();
  }
}
